import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { valoreWebSocket, ValorePriceData } from '../services/valoreWebSocket';
import { isValoreSupported } from '../constants/valoreSymbols';

interface ValorePriceContextType {
  prices: Map<string, ValorePriceData>;
  connectionStatus: string;
  isConnected: boolean;
  getPriceBySymbol: (symbol: string) => number;
  getBidPrice: (symbol: string) => number;
  getAskPrice: (symbol: string) => number;
  getPriceData: (symbol: string) => ValorePriceData | null;
  isValoreSymbol: (symbol: string) => boolean;
  lastUpdateTime: number;
}

const ValorePriceContext = createContext<ValorePriceContextType | undefined>(undefined);

interface ValorePriceProviderProps {
  children: React.ReactNode;
}

export const ValorePriceProvider: React.FC<ValorePriceProviderProps> = ({ children }) => {
  const [prices, setPrices] = useState<Map<string, ValorePriceData>>(() => valoreWebSocket.getAllPrices());
  const [connectionStatus, setConnectionStatus] = useState<string>(() => valoreWebSocket.getStatus());
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(Date.now());

  useEffect(() => {
    valoreWebSocket.connect();

    const unsubscribePrices = valoreWebSocket.subscribe((newPrices) => {
      setPrices(new Map(newPrices));
      setLastUpdateTime(Date.now());
    });

    const unsubscribeStatus = valoreWebSocket.onStatusChange((status) => {
      setConnectionStatus(status);
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !valoreWebSocket.isConnected()) {
        valoreWebSocket.connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Periodic forced refresh to ensure UI updates
    const refreshInterval = setInterval(() => {
      const currentPrices = valoreWebSocket.getAllPrices();
      if (currentPrices.size > 0) {
        setPrices(new Map(currentPrices));
        setLastUpdateTime(Date.now());
      }
    }, 1000);

    return () => {
      unsubscribePrices();
      unsubscribeStatus();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(refreshInterval);
    };
  }, []);

  const getPriceBySymbol = useCallback((symbol: string): number => {
    const normalizedSymbol = symbol.replace('/', '');
    const priceData = prices.get(normalizedSymbol);
    return priceData?.mid || 0;
  }, [prices]);

  const getBidPrice = useCallback((symbol: string): number => {
    const normalizedSymbol = symbol.replace('/', '');
    const priceData = prices.get(normalizedSymbol);
    return priceData?.bid || 0;
  }, [prices]);

  const getAskPrice = useCallback((symbol: string): number => {
    const normalizedSymbol = symbol.replace('/', '');
    const priceData = prices.get(normalizedSymbol);
    return priceData?.ask || 0;
  }, [prices]);

  const getPriceData = useCallback((symbol: string): ValorePriceData | null => {
    const normalizedSymbol = symbol.replace('/', '');
    return prices.get(normalizedSymbol) || null;
  }, [prices]);

  const isValoreSymbol = useCallback((symbol: string): boolean => {
    return isValoreSupported(symbol);
  }, []);

  const contextValue: ValorePriceContextType = {
    prices,
    connectionStatus,
    isConnected: connectionStatus === 'connected',
    getPriceBySymbol,
    getBidPrice,
    getAskPrice,
    getPriceData,
    isValoreSymbol,
    lastUpdateTime
  };

  return (
    <ValorePriceContext.Provider value={contextValue}>
      {children}
    </ValorePriceContext.Provider>
  );
};

export const useValorePrices = (): ValorePriceContextType => {
  const context = useContext(ValorePriceContext);
  if (context === undefined) {
    throw new Error('useValorePrices must be used within a ValorePriceProvider');
  }
  return context;
};
